import { useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
}

export function Modal({ open, onClose, title, children }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, handleKeyDown])

  // Focus trap: focus modal on open
  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.focus()
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[var(--color-ember)]/40 backdrop-blur-[2px] z-50"
            onClick={onClose}
            aria-hidden="true"
          />
          {/* Wrapper handles positioning so Framer Motion's transforms (y on
              the inner motion.div) don't fight the centering translate. */}
          <div className="fixed inset-0 z-50 flex items-end justify-center md:items-center pointer-events-none">
            <motion.div
              ref={modalRef}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              // Responsive: bottom-sheet on mobile, centred dialog on md+.
              className="pointer-events-auto bg-[var(--surface)] rounded-t-3xl md:rounded-2xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto max-w-lg w-full md:w-[min(32rem,calc(100vw-3rem))] shadow-[var(--shadow-modal)]"
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? 'modal-title' : undefined}
              tabIndex={-1}
            >
            {title && (
              <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-subtle)]">
                <h3 id="modal-title" className="font-bold text-[var(--text-primary)] text-lg">
                  {title}
                </h3>
                <button
                  onClick={onClose}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            <div className="px-5 py-4">{children}</div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
